import React from 'react';
import { JcCommonProps, JcSize, JcUtils } from '../utils/JcUtils';
import { shouldInterceptLinkClick } from '../utils/JcLinkUtils';
import { JcButtonAppearance, JcButtonType } from '../components/button/JcButton';
import { JcBuiltInIcon } from '../components/icon/JcBuiltInIcons';
import { JcIcon, JcIconType } from '../components/icon/JcIcon';
import { JcLink } from '../components/JcLink';
import { JcAppRoute } from './JcAppRoute';
import { JcAppRoutingConfig, RouteProps, RouteState } from './JcAppRoutingConfig';
import { JcAppNavigation, useJcAppNavigation } from './JcAppNavigation';


export interface JcRouteLocationProps<
    RoutesT extends Record<string, JcAppRoute<any, any>>,
    K extends keyof RoutesT
> {
    readonly route: K;
    readonly routeProps: RouteProps<RoutesT[K]>;
    readonly label: React.ReactNode;
    readonly replace?: boolean;
    /** Fast-path hint carried via history state; see {@link JcAppNavigateOptions.state}. */
    readonly state?: RouteState<RoutesT[K]>;
    readonly target?: React.HTMLAttributeAnchorTarget;
    readonly rel?: string;
    readonly unstyled?: boolean;
}

export interface JcRouteLinkProps<
    RoutesT extends Record<string, JcAppRoute<any, any>>,
    K extends keyof RoutesT
> extends JcCommonProps, JcButtonAppearance, JcRouteLocationProps<RoutesT, K> {
    /** Accessible name, when the visible label doesn't say where the link goes. */
    readonly ariaLabel?: string;
}

/**
 * Creates a typed route-aware link component bound to a routing config.
 *
 *   export const AppRouteLink = makeJcRouteLink(appRoutingConfig);
 *
 *   <AppRouteLink route="SEASON" routeProps={{ seasonId }} label="View" />
 *
 * The rendered `<a>` has a real `href`, so middle-click / Cmd-click / right-click
 * "Open in new tab" all work natively. Plain left-clicks are intercepted and
 * routed via the SPA navigation hook.
 */
export function makeJcRouteLink<RoutesT extends Record<string, JcAppRoute<any, any>>>(
    config: JcAppRoutingConfig<RoutesT>
) {
    return function JcRouteLink<K extends keyof RoutesT>(
        props: JcRouteLinkProps<RoutesT, K>
    ): React.ReactElement {
        const { navigateToRoute } = useJcAppNavigation(config);
        const { route, routeProps, replace, state, ...linkProps } = props;
        const href = JcAppNavigation.generatePath(config, route, routeProps);

        const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
            if (shouldInterceptLinkClick(event, props.target)) {
                event.preventDefault();
                navigateToRoute(route, routeProps, { replace, state });
            }
        };

        return <JcLink {...linkProps} href={href} onClick={handleClick} />;
    };
}

export interface JcRouteIconLinkProps<
    RoutesT extends Record<string, JcAppRoute<any, any>>,
    K extends keyof RoutesT
> extends JcCommonProps, Omit<JcRouteLocationProps<RoutesT, K>, 'label' | 'unstyled'> {
    readonly icon: JcBuiltInIcon;
    /** Accessible name — the link has no visible text. */
    readonly ariaLabel?: string;
}

/**
 * Creates a typed icon-only route link wearing `JcIconButton` chrome — e.g. a
 * title-bar edit pencil. A link, not a button, since it navigates.
 */
export function makeJcRouteIconLink<RoutesT extends Record<string, JcAppRoute<any, any>>>(
    config: JcAppRoutingConfig<RoutesT>
) {
    const RouteLink = makeJcRouteLink(config);
    return function JcRouteIconLink<K extends keyof RoutesT>(
        props: JcRouteIconLinkProps<RoutesT, K>
    ): React.ReactElement {
        const { icon, className, ...linkProps } = props;
        return (
            <RouteLink
                {...linkProps}
                className={JcUtils.classNameString(['jc-icon-button', 'jc-icon-button-size-small', className])}
                buttonType={JcButtonType.GHOST}
                label={<JcIcon type={JcIconType.BUILT_IN} icon={icon} size={JcSize.NONE} />}
            />
        );
    };
}
